import React, { useCallback } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { NetworkStatus } from 'apollo-client';

import { usePubSubEvent } from '@confluence/pubsub-client/entry-points/usePubSubEvent';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { isNotFoundError } from '@confluence/error-boundary/entry-points/isNotFoundError';
import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations';
import { fg } from '@confluence/feature-gating';

// eslint-disable-next-line graphql-relay-compat/no-experimental-directives -- Read http://go/cfe-no-experimental-directives
import { TitleContentPropertyMutation } from './TitleContentPropertyMutation.experimentalgraphql';
import { TitleContentPropertyQuery } from './TitleContentPropertyQuery.graphql';
import { contentPropertyHelper } from './contentPropertyHelper';
import type { TitleContentPropertyMutation as TitleContentPropertyMutationType } from './__types__/TitleContentPropertyMutation';
import type {
	TitleContentPropertyQuery as TitleContentPropertyQueryType,
	TitleContentPropertyQueryVariables,
} from './__types__/TitleContentPropertyQuery';
import {
	TitleAlignmentType,
	defaultPropertyValue,
	COVERPICTUREWIDTH,
} from './contentTopperConstants';

type UseTitleContentPropertiesForEditorProps = {
	contentId: string;
	propertyKey?: string;
	isDelete?: boolean;
	loadTitleContentProperties?: boolean;
	updateAlignmentState?: (alignment?: TitleAlignmentType) => void;
	updateCoverPictureWidthState?: (coverPictureWidth: COVERPICTUREWIDTH) => void;
	contentType: 'page' | 'blogpost' | null;
	flags?: FlagsStateContainer;
	updateIsLoadingComplete?: (isLoadingComplete: boolean) => void;
};

export const getPageTitleDraftTitlePropertyContent = (
	queryData: TitleContentPropertyQueryType | null,
) =>
	queryData?.content?.nodes?.[0]?.pageTitleDraft?.nodes?.find(
		(node) => node?.key === 'page-title-property-draft',
	);

const i18n = defineMessages({
	errorFlagTitle: {
		id: 'content-topper.page-title.alignment.error.title',
		defaultMessage: 'We couldn’t update the title alignment',
		description:
			"Title for error flag when a user tries to change title alignment and there's an error",
	},
	errorFlagDescription: {
		id: 'content-topper.page-title.alignment.error.description',
		defaultMessage: 'Try again, or refresh the page.',
		description:
			"Description for error flag when a user tries to change title alignment and there's an error",
	},
});

export const useTitleContentPropertiesForEditor = ({
	contentId,
	propertyKey = 'page-title-property-draft',
	isDelete = false,
	loadTitleContentProperties = false,
	updateAlignmentState,
	updateCoverPictureWidthState,
	contentType,
	flags,
	updateIsLoadingComplete,
}: UseTitleContentPropertiesForEditorProps) => {
	const isInitialLoadComplete = React.useRef(false);
	const isTitleCenteredInitial = React.useRef(false);

	const handleError = useCallback(
		(error: any, suppressErrors: boolean) => {
			const isUnauthOrNotFoundError = isUnauthorizedError(error) || isNotFoundError(error);
			if (!isUnauthOrNotFoundError) {
				getMonitoringClient().submitError(error, {
					attribution: Attribution.COMPANY_HUB,
				});
			}
			markErrorAsHandled(error);
			if (!suppressErrors) {
				void flags?.showErrorFlag({
					title: <FormattedMessage {...i18n.errorFlagTitle} />,
					description: <FormattedMessage {...i18n.errorFlagDescription} />,
				});
			}
		},
		[flags],
	);

	const getAlignmentFromContentProperty = (response: any): TitleAlignmentType => {
		const titlePropertyContent = getPageTitleDraftTitlePropertyContent(response);
		if (titlePropertyContent?.value) {
			try {
				return (
					contentPropertyHelper(titlePropertyContent)?.titleLayoutAlignment ||
					defaultPropertyValue.titleLayoutAlignment
				);
			} catch (e) {
				handleError(e, false);
				return defaultPropertyValue.titleLayoutAlignment;
			}
		} else {
			return defaultPropertyValue.titleLayoutAlignment;
		}
	};

	const onCompleted = (response: any) => {
		const coverPictureWidth: COVERPICTUREWIDTH =
			response.content?.nodes?.[0]?.metadata?.frontend?.coverPictureWidth || COVERPICTUREWIDTH.FULL;
		void updateAlignmentState?.(getAlignmentFromContentProperty(response));
		void updateCoverPictureWidthState?.(coverPictureWidth);
	};

	const {
		data,
		networkStatus,
		refetch: refetchProperties,
	} = useQuery<
		TitleContentPropertyQueryType,
		TitleContentPropertyQueryVariables

		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
	>(TitleContentPropertyQuery, {
		variables: {
			contentId,
			status: ['current', 'draft', 'archived'],
		},
		fetchPolicy: Boolean(window.__SSR_RENDERED__) ? 'cache-first' : 'cache-and-network', // cache-and-network is required for: edit cover width, transition out of edit, transition back to edit for same page (for Live Docs and Classic pages)
		onError: (error) => {
			handleError(error, true);
		},
		skip: !(loadTitleContentProperties || Boolean(window.__SSR_RENDERED__)),
		notifyOnNetworkStatusChange: true,
	});
	const isTitleAlignmentLoading = networkStatus === NetworkStatus.loading && !data;
	const refetchLoading = networkStatus === NetworkStatus.refetch;
	const coverImageWidthCached = data?.content?.nodes?.[0]?.metadata?.frontend?.coverPictureWidth;

	isTitleCenteredInitial.current =
		(Boolean(window.__SSR_RENDERED__) || !isTitleAlignmentLoading) &&
		getAlignmentFromContentProperty(data) === TitleAlignmentType.CENTER;
	if (!isInitialLoadComplete.current && loadTitleContentProperties && data) {
		isInitialLoadComplete.current = true;
		onCompleted(data);
		updateIsLoadingComplete?.(true);
	}

	const [updateProperties, { loading: isUpdatingAlignment }] =
		useMutation<TitleContentPropertyMutationType>(
			// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Experimental Schema GraphQL Operation
			TitleContentPropertyMutation,
		);

	const onUpdatedPageTitleProperty = useCallback(
		(_eventName: any, _pubSubData: any) => {
			if (
				!['DUAL', 'GRAPHQL'].includes(
					StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', ''),
				) ||
				!fg('cc_fps_migration_page-title-property-updated')
			) {
				try {
					const newContentState = JSON.parse(_pubSubData?.contentPropertyUpdateRequestBody?.value);
					void updateAlignmentState?.(newContentState?.titleLayoutAlignment);
				} catch (error) {
					handleError(error, false);
				}
			}
		},
		[updateAlignmentState, handleError],
	);
	const onPageTitlePropertyUpdatedSubscriptionData = useCallback(
		(_eventName: string, response: any) => {
			const { pageTitlePropertyUpdated } = response || {};
			try {
				const value = JSON.parse(pageTitlePropertyUpdated?.value);
				void updateAlignmentState?.(value?.titleLayoutAlignment);
			} catch (error) {
				getMonitoringClient().submitError(error, {
					attribution: Attribution.PAGE_EXPERIENCES,
				});
				markErrorAsHandled(error);
			}
		},
		[updateAlignmentState],
	);

	usePubSubEvent({
		contentId,
		contentType: contentType || 'page',
		eventName: 'updated:pageTitleProperty',
		onEvent: onUpdatedPageTitleProperty,
		skip:
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			fg('cc_fps_migration_page-title-property-updated') &&
			fg('confluence_enable_subscriptions-context-provider') &&
			StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', '') ===
				'GRAPHQL',
	});

	useSubscriptionEvent({
		contentId,
		contentType: contentType || 'page',
		delta: 'pageTitlePropertyUpdated',
		onDataReceived: onPageTitlePropertyUpdatedSubscriptionData,
		skip:
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			!fg('cc_fps_migration_page-title-property-updated') ||
			!fg('confluence_enable_subscriptions-context-provider') ||
			!['DUAL', 'GRAPHQL'].includes(
				StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', ''),
			),
		subscriberId: 'useTitleContentPropertiesForEditor',
	});

	const updatePageTitleProperty = useCallback(
		async (propertyValue: any) => {
			try {
				const getVersionNumber = (data: any) =>
					data?.content?.nodes?.[0]?.pageTitleDraft?.nodes?.[0]?.version?.number || 0;
				const versionData = await refetchProperties();
				const versionNumber = getVersionNumber(versionData.data);
				const newVersionNumber = versionNumber ? versionNumber + 1 : 1;

				const optimisticResponse = {
					__typename: 'Mutation',
					experimentalUpdateContentProperty: {
						__typename: 'experimentalUpdateContentProperty',
						success: true,
						id: contentId,
						key: propertyKey,
						value: propertyValue,
					},
				};
				await updateProperties({
					variables: {
						contentId,
						propertyKey,
						isDelete,
						propertyValue,
						versionNumber: String(newVersionNumber),
					},
					optimisticResponse,
					update: (proxy: any) => {
						try {
							const cacheData = proxy.readQuery({
								query: TitleContentPropertyQuery,
								variables: {
									contentId,
									status: ['current', 'draft', 'archived'],
								},
							});

							cacheData!.content!.nodes![0]!.pageTitleDraft!.nodes![0] = {
								...cacheData!.content!.nodes![0]!.pageTitleDraft!.nodes![0],
								id: contentId,
								key: propertyKey,
								value: JSON.stringify(propertyValue),
								version: {
									number: newVersionNumber,
									__typename: 'Version',
								},
							};

							proxy.writeQuery({
								query: TitleContentPropertyQuery,
								variables: {
									contentId,
									status: ['current', 'draft', 'archived'],
								},
								data,
							});
						} catch (error) {
							throw new Error(
								` failed to write to cache while updating ${propertyKey} content property after user updated page title${error.message}`,
							);
						}
					},
				});
			} catch (err) {
				handleError(err, false);
			}
		},
		[propertyKey, contentId, isDelete, updateProperties, refetchProperties, data, handleError],
	);

	return {
		isUpdatingAlignment: isUpdatingAlignment || refetchLoading,
		updatePageTitleProperty,
		// exported for testing purposes
		onCompleted,
		isTitleAlignmentLoading,
		isTitleCenteredInitial: isTitleCenteredInitial.current,
		coverImageWidthCached: coverImageWidthCached as COVERPICTUREWIDTH,
	};
};
